import React, { useEffect, useMemo, useRef, useState } from "react";
import mermaid from "mermaid";
import { ZoomIn, ZoomOut, RotateCcw } from "lucide-react";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import UnfoldMoreRoundedIcon from "@mui/icons-material/UnfoldMoreRounded";
import UnfoldLessRoundedIcon from "@mui/icons-material/UnfoldLessRounded";
import SummaryPanel from "./SummaryPanel";

if (typeof window !== "undefined") {
  if (!window.__MERMAID_INITIALIZED__) {
    mermaid.initialize({
      startOnLoad: false,
      theme: "base",
      securityLevel: "loose",
      suppressErrorRendering: true,
      flowchart: { curve: "basis", useMaxWidth: false, htmlLabels: true },
      er: { useMaxWidth: false },
    });

    window.__MERMAID_INITIALIZED__ = true;
  }
}

// Helper to parse subgraphs and node structure from mermaid text
const parseSubgraphs = (diagramText) => {
  const lines = diagramText
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const subgraphs = [];
  const allNodes = {};
  const edges = [];
  let header = "";
  const stack = [];

  const parseSubgraphStart = (line) => {
    const match = line.match(
      /^subgraph\s+([^\s\[]+)(?:\s*\[(?:"([^"]+)"|([^\]]+))\])?\s*$/i
    );
    if (!match) return null;

    const id = match[1]?.trim();
    const label = (match[2] || match[3] || id || "").trim();
    if (!id) return null;

    return { id, label: label || id };
  };

  lines.forEach((line) => {
    if (
      !header &&
      (line.startsWith("flowchart") ||
        line.startsWith("graph") ||
        line.startsWith("erdiagram"))
    ) {
      header = line;
      return;
    }

    const topLevel = stack[0] || null;
    const subgraphStart = parseSubgraphStart(line);

    if (subgraphStart) {
      if (topLevel && stack.length >= 1) {
        // Preserve nested subgraph definitions in the top-level group body.
        topLevel.lines.push(line);
      }

      const context = {
        id: subgraphStart.id,
        label: subgraphStart.label,
        lines: [],
        nodeIds: [],
      };

      stack.push(context);

      if (stack.length === 1) {
        subgraphs.push(context);
      }

      return;
    }

    if (line === "end") {
      if (stack.length > 1 && topLevel) {
        topLevel.lines.push(line);
      }

      if (stack.length > 0) {
        stack.pop();
      }

      return;
    }

    if (topLevel) {
      topLevel.lines.push(line);
    }

    // Extract nodes declared as ID["label"] or ID[label].
    const nodeMatch = line.match(/^([A-Za-z0-9_]+)\s*\[(?:"([^"]+)"|([^\]]+))\]/);
    if (nodeMatch) {
      const [, id, quotedLabel, plainLabel] = nodeMatch;
      allNodes[id] = { id, label: quotedLabel || plainLabel || id };
      if (topLevel && !topLevel.nodeIds.includes(id)) {
        topLevel.nodeIds.push(id);
      }
    }

    // Extract simple directional edges.
    if (/-+>|=+>/.test(line)) {
      const sourceMatch = line.match(/^([A-Za-z0-9_]+)/);
      const targetMatch = line.match(/([A-Za-z0-9_]+)\s*$/);
      if (sourceMatch && targetMatch) {
        edges.push({
          source: sourceMatch[1],
          target: targetMatch[1],
          line,
          ownerSubgraph: topLevel ? topLevel.id : null,
        });
      }
    }
  });

  return { header, subgraphs, allNodes, edges };
};

// Rebuild diagram with some subgraphs collapsed
const rebuildDiagram = (parsed, collapsedSubgraphs) => {
  const collapsed = new Set(collapsedSubgraphs);
  const lines = [parsed.header, ""];
  const seenEdges = new Set();
  const subgraphById = new Map(parsed.subgraphs.map((s) => [s.id, s]));

  const formatSubgraphLine = (subgraph) => {
    const label = (subgraph.label || subgraph.id || "").replace(/"/g, '\\"');
    if (!label || label === subgraph.id) {
      return `subgraph ${subgraph.id}`;
    }
    return `subgraph ${subgraph.id}["${label}"]`;
  };

  parsed.subgraphs.forEach((subgraph) => {
    if (collapsed.has(subgraph.id)) {
      const nodeCount = subgraph.nodeIds.length;
      const collapsedId = `COLLAPSED_${subgraph.id}`;
      const collapsedLabel = (subgraph.label || subgraph.id || "").replace(/"/g, '\\"');
      lines.push(`${collapsedId}["${collapsedLabel}<br/><small>${nodeCount} nodes</small>"]`);
    } else {
      lines.push(formatSubgraphLine(subgraph));
      subgraph.lines.forEach((line) => lines.push(line));
      lines.push("end");
    }
    lines.push("");
  });

  // Add edges, updating source/target if they're in collapsed subgraphs
  parsed.edges.forEach((edge) => {
    let source = edge.source;
    let target = edge.target;

    // Find which subgraph contains each node
    for (const subgraph of parsed.subgraphs) {
      if (collapsed.has(subgraph.id)) {
        if (subgraph.nodeIds.includes(source)) {
          source = `COLLAPSED_${subgraph.id}`;
        }
        if (subgraph.nodeIds.includes(target)) {
          target = `COLLAPSED_${subgraph.id}`;
        }
      }
    }

    if (source === target) return; // Skip self-edges

    // Skip intra-subgraph edges already present inside the expanded subgraph block
    if (edge.ownerSubgraph && !collapsed.has(edge.ownerSubgraph)) return;

    // If an edge originates from a nested subgraph, resolve to the top-level owner.
    if (edge.ownerSubgraph && !subgraphById.has(edge.ownerSubgraph)) {
      return;
    }

    const line = edge.line.replace(edge.source, source).replace(edge.target, target);
    if (!seenEdges.has(`${source}-${target}`)) {
      seenEdges.add(`${source}-${target}`);
      lines.push(line);
    }
  });

  return lines.join("\n");
};

const normalizeKey = (value) =>
  (value || "")
    .toString()
    .toLowerCase()
    .replace(/<br\s*\/?>(\s*)/g, " ")
    .replace(/[^a-z0-9]+/g, "")
    .trim();

const extractSvgNodeId = (group) => {
  if (!group) return "unknown-node";

  const dataId = group.getAttribute("data-id");
  if (dataId) return dataId;

  const rawId = group.getAttribute("id") || "";
  if (!rawId) return "unknown-node";

  const collapsedMatch = rawId.match(/(?:^|-)COLLAPSED_([A-Za-z0-9_]+)(?:-\d+)?$/i);
  if (collapsedMatch?.[1]) return `COLLAPSED_${collapsedMatch[1]}`;

  const flowchartMatch = rawId.match(/flowchart-([A-Za-z_]+\d+)-\d+$/i);
  if (flowchartMatch?.[1]) return flowchartMatch[1];

  const genericMatch = rawId.match(/(?:^|[-_])([A-Za-z_]+\d+)(?:[-_]|$)/i);
  if (genericMatch?.[1]) return genericMatch[1];

  return rawId;
};

export default function MermaidDiagram({ mermaidText, modalKey, summaries = [] }) {
  const MIN_ZOOM = 0.05;
  const MAX_ZOOM = 12;
  const ZOOM_FACTOR = 1.25;
  const DEFAULT_ZOOM = 0.25;
  const DRAG_CLICK_THRESHOLD = 6;

  const ref = useRef(null);
  const scaleWrapperRef = useRef(null);
  const interactionCleanupRef = useRef([]);
  const selectedSvgNodeRef = useRef(null);
  const diagramText = mermaidText?.trim() ? mermaidText : "";
  const parsedDiagram = useMemo(() => parseSubgraphs(diagramText), [diagramText]);

  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [panelOpen, setPanelOpen] = useState(true);
  const [selectedNode, setSelectedNode] = useState(null);
  const [collapsedSubgraphs, setCollapsedSubgraphs] = useState(new Set());
  const [isGroupPanelOpen, setIsGroupPanelOpen] = useState(true);
  const initializedDiagramRef = useRef(null);
  const pendingAnchorRef = useRef(null);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const panStartRef = useRef({ x: 0, y: 0 });
  const dragDistanceRef = useRef(0);
  const suppressClickUntilRef = useRef(0);
  const toggleSubgraphRef = useRef(null);
  const panRef = useRef(pan);
  const zoomRef = useRef(zoom);
  const summariesRef = useRef(summaries);
  const parsedRef = useRef(null);
  const autoResetDoneForKeyRef = useRef(null);
  summariesRef.current = summaries;
  panRef.current = pan;
  zoomRef.current = zoom;

  useEffect(() => {
    if (!diagramText) {
      initializedDiagramRef.current = null;
      setCollapsedSubgraphs(new Set());
      return;
    }

    const diagramKey = `${modalKey || "default"}::${diagramText}`;
    if (initializedDiagramRef.current === diagramKey) {
      return;
    }

    parsedRef.current = parsedDiagram;
    setCollapsedSubgraphs(new Set(parsedDiagram.subgraphs.map((s) => s.id)));
    setSelectedNode(null);
    initializedDiagramRef.current = diagramKey;
  }, [diagramText, modalKey, parsedDiagram]);

  const toggleSubgraph = (subgraphId) => {
    if (!subgraphId) return;
    setCollapsedSubgraphs((prev) => {
      const next = new Set(prev);
      if (next.has(subgraphId)) {
        next.delete(subgraphId);
      } else {
        next.add(subgraphId);
      }
      return next;
    });
    setSelectedNode(null);
    selectedSvgNodeRef.current = null;
  };

  toggleSubgraphRef.current = toggleSubgraph;

  const collapseAllSubgraphs = () => {
    setCollapsedSubgraphs(new Set(parsedDiagram.subgraphs.map((s) => s.id)));
    setSelectedNode(null);
    selectedSvgNodeRef.current = null;
  };

  const expandAllSubgraphs = () => {
    setCollapsedSubgraphs(new Set());
    setSelectedNode(null);
    selectedSvgNodeRef.current = null;
  };

  const areAllSubgraphsCollapsed =
    parsedDiagram.subgraphs.length > 0 &&
    collapsedSubgraphs.size === parsedDiagram.subgraphs.length;

  const toggleAllSubgraphs = () => {
    if (areAllSubgraphsCollapsed) {
      expandAllSubgraphs();
    } else {
      collapseAllSubgraphs();
    }
  };

  const cleanupSvgInteractions = () => {
    interactionCleanupRef.current.forEach((fn) => fn());
    interactionCleanupRef.current = [];
    selectedSvgNodeRef.current = null;
  };

  const getTopLevelNodeGroups = (svgEl) => {
    const byClass = Array.from(svgEl.querySelectorAll("g.node, g.entity"));
    const erEntityParents = Array.from(
      svgEl.querySelectorAll("rect.er.entityBox, rect.entityBox, .er.entityBox")
    )
      .map((el) => el.closest("g[id]") || el.parentElement?.closest("g"))
      .filter(Boolean);
    const all = [...new Set([...byClass, ...erEntityParents])];
    return all.filter(
      (group) => !all.some((other) => other !== group && other.contains(group))
    );
  };

  const applyNodeHighlight = (node) => {
    const svgEl = ref.current?.querySelector("svg");
    if (!svgEl) return;

    const candidateNodes = getTopLevelNodeGroups(svgEl);

    candidateNodes.forEach((group) => {
      const shapes = group.querySelectorAll("rect, polygon, path, circle, ellipse");
      const isSelected = node && group === node;
      const groupNodeId = extractSvgNodeId(group);
      const isCollapsedGroupNode = groupNodeId.startsWith("COLLAPSED_");
      group.style.opacity = "1";
      shapes.forEach((shape) => {
        if (isSelected) {
          shape.style.filter = "drop-shadow(0 0 8px rgba(59,130,246,.65))";
          shape.style.strokeWidth = "3px";
          shape.style.stroke = "#2563eb";
        } else if (isCollapsedGroupNode) {
          shape.style.fill = "#fef3c7";
          shape.style.stroke = "#d97706";
          shape.style.strokeWidth = "2.5px";
          shape.style.strokeDasharray = "6 3";
          shape.style.filter = "drop-shadow(0 0 6px rgba(217,119,6,.25))";
        } else {
          shape.style.filter = "";
          shape.style.strokeWidth = "";
          shape.style.stroke = "";
          shape.style.strokeDasharray = "";
          shape.style.fill = "";
        }
      });
    });
  };

  const normalizeForMatch = (value) => normalizeKey(value);

  const extractCanonicalId = (rawId, label) => {
    const sourceId = (rawId || "").toString();
    const sourceLabel = (label || "").toString();

    const summaryNames = (summariesRef.current || [])
      .map((s) => (s?.function_name || "").toString().trim())
      .filter(Boolean);

    const normalizedRaw = normalizeForMatch(sourceId);
    const normalizedLabel = normalizeForMatch(sourceLabel);

    const rawToken =
      sourceId.match(/\b([A-Za-z_]+\d+)\b/)?.[1] ||
      sourceId.match(/(?:^|[-_])([A-Za-z_]+\d+)(?:[-_]|$)/)?.[1] ||
      sourceId;
    const normalizedRawToken = normalizeForMatch(rawToken);

    const exactSummaryName = summaryNames.find((name) => {
      const normalizedName = normalizeForMatch(name);
      return (
        normalizedName === normalizedRawToken ||
        normalizedName === normalizedRaw ||
        normalizedName === normalizedLabel ||
        normalizedName === normalizeForMatch(sourceLabel.split("\n")[0])
      );
    });
    if (exactSummaryName) return exactSummaryName;

    const withoutPrefix = sourceId
      .replace(/^(?:flowchart|graph|er|entity|node)-/i, "")
      .replace(/-(?:\d+|\d+-\d+)$/i, "")
      .replace(/-([a-f0-9]{6,})$/i, "");

    const keyLikeToken = withoutPrefix.match(/\b([A-Za-z_]+\d+)\b/);
    if (keyLikeToken?.[1]) return keyLikeToken[1];

    if (withoutPrefix && !withoutPrefix.includes(" ")) return withoutPrefix;

    return sourceLabel.split("\n")[0].trim() || sourceId || "unknown-node";
  };

  const resolveSummary = (id, label) => {
    if (!Array.isArray(summariesRef.current) || !summariesRef.current.length) {
      return null;
    }

    const normalize = (value) =>
      (value || "")
        .toString()
        .toLowerCase()
        .replace(/<br\s*\/?>(\s*)/g, " ")
        .replace(/[^a-z0-9]+/g, "")
        .trim();

    const maybeExtractCoreId = (value) => {
      const raw = (value || "").toString();
      const direct = raw.match(/\b([A-Za-z]+\d+)\b/);
      if (direct) return direct[1];

      const dashed = raw.match(/(?:^|[-_])([A-Za-z]+\d+)(?:[-_]|$)/);
      if (dashed) return dashed[1];

      return raw;
    };

    const candidates = [
      id,
      maybeExtractCoreId(id),
      label,
      (label || "").split("\n")[0],
      (label || "").split("(")[0],
    ]
      .map((value) => (value || "").toString().trim())
      .filter(Boolean);

    const normalizedCandidates = Array.from(
      new Set(candidates.map(normalize).filter(Boolean))
    );

    const withKeys = summariesRef.current.map((summary) => ({
      summary,
      keyRaw: (summary?.function_name || "").toString(),
      keyNorm: normalize(summary?.function_name),
    }));

    const exactMatch = withKeys.find(({ keyNorm }) =>
      normalizedCandidates.includes(keyNorm)
    );
    if (exactMatch) return exactMatch.summary;

    const containsMatch = withKeys.find(({ keyNorm }) =>
      normalizedCandidates.some(
        (candidate) =>
          keyNorm.length >= 4 && candidate.length >= 4 &&
          (candidate.includes(keyNorm) || keyNorm.includes(candidate))
      )
    );

    return containsMatch?.summary || null;
  };

  const attachSvgInteractions = () => {
    const svgEl = ref.current?.querySelector("svg");
    if (!svgEl) return;

    const candidateNodes = getTopLevelNodeGroups(svgEl);

    candidateNodes.forEach((group) => {
      group.style.cursor = "pointer";

      const groupNodeId = extractSvgNodeId(group);
      const isCollapsedGroupNode = groupNodeId.startsWith("COLLAPSED_");
      if (isCollapsedGroupNode) {
        const shapes = group.querySelectorAll("rect, polygon, path, circle, ellipse");
        shapes.forEach((shape) => {
          shape.style.fill = "#fef3c7";
          shape.style.stroke = "#d97706";
          shape.style.strokeWidth = "2.5px";
          shape.style.strokeDasharray = "6 3";
          shape.style.filter = "drop-shadow(0 0 6px rgba(217,119,6,.25))";
        });
      }

      if (!group.querySelector("title")) {
        const textLabel =
          group.querySelector(".er.entityLabel, .nodeLabel")?.textContent?.trim() ||
          group.querySelector("text")?.textContent?.trim() ||
          group.querySelector("foreignObject")?.textContent?.trim() ||
          group.querySelector("p, span, div")?.textContent?.trim();
        if (textLabel) {
          const titleEl = document.createElementNS("http://www.w3.org/2000/svg", "title");
          titleEl.textContent = textLabel;
          group.appendChild(titleEl);
        }
      }

      const handleClick = (event) => {
        if (Date.now() < suppressClickUntilRef.current) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        const rawNodeId = extractSvgNodeId(group);
        const nodeLabel =
          group.querySelector(".er.entityLabel, .nodeLabel")?.textContent?.trim() ||
          group.querySelector("text")?.textContent?.trim() ||
          group.querySelector("foreignObject")?.textContent?.trim() ||
          group.querySelector("p, span, div")?.textContent?.trim() ||
          rawNodeId
            .replace(/^(?:flowchart|graph|er|entity|node)-/i, "")
            .replace(/-\d+$/i, "") ||
          rawNodeId;

        if (rawNodeId.startsWith("COLLAPSED_")) {
          const subgraphId = rawNodeId.replace(/^COLLAPSED_/, "");
          toggleSubgraphRef.current?.(subgraphId);
          return;
        }

        // Regular node click handling
        const isSameNode = selectedSvgNodeRef.current === group;
        const nextNode = isSameNode ? null : group;
        selectedSvgNodeRef.current = nextNode;
        applyNodeHighlight(selectedSvgNodeRef.current);

        if (!nextNode) {
          setSelectedNode(null);
          return;
        }

        const nodeId = extractCanonicalId(rawNodeId, nodeLabel);
        setSelectedNode({
          id: nodeId,
          data: {
            label: nodeLabel,
            summary: resolveSummary(nodeId, nodeLabel) || resolveSummary(rawNodeId, nodeLabel),
          },
        });
        setPanelOpen(true);
      };

      group.addEventListener("click", handleClick);
      interactionCleanupRef.current.push(() => {
        group.removeEventListener("click", handleClick);
      });
    });
  };

  const attachClusterClickToggles = (svgEl) => {
    // Collapse expanded clusters on click
    const clusters = Array.from(svgEl.querySelectorAll("g.cluster"));
    clusters.forEach((cluster) => {
      const labelText =
        cluster.querySelector(".cluster-label")?.textContent?.trim() ||
        cluster.querySelector("text")?.textContent?.trim() ||
        "";
      const idAttr = cluster.getAttribute("id") || "";

      const matched = parsedDiagram.subgraphs.find((s) => {
        const normalizedLabel = normalizeKey(s.label || s.id);
        return (
          normalizeKey(labelText) === normalizeKey(s.id) ||
          normalizeKey(labelText) === normalizedLabel ||
          idAttr.includes(s.id)
        );
      });
      if (!matched) return;

      cluster.style.cursor = "pointer";

      const titleEl = document.createElementNS("http://www.w3.org/2000/svg", "title");
      titleEl.textContent = `Collapse ${matched.id}`;
      cluster.appendChild(titleEl);

      const handleClusterClick = (e) => {
        if (Date.now() < suppressClickUntilRef.current) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        e.preventDefault();
        e.stopPropagation();
        toggleSubgraphRef.current?.(matched.id);
      };

      cluster.addEventListener("click", handleClusterClick);
      interactionCleanupRef.current.push(() => {
        cluster.removeEventListener("click", handleClusterClick);
      });
    });
  };

  useEffect(() => {
    if (!diagramText || !ref.current) return;

    parsedRef.current = parsedDiagram;

    cleanupSvgInteractions();
    ref.current.innerHTML = "";

    const diagramId = `mermaid-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const diagramKey = `${modalKey || "default"}::${diagramText}`;
    const firstLine = diagramText.trim().split("\n")[0].trim().toLowerCase();
    if (
      !firstLine.startsWith("erdiagram") &&
      !firstLine.startsWith("graph") &&
      !firstLine.startsWith("flowchart")
    ) {
      console.warn("MermaidDiagram: unsupported diagram type", firstLine);
      return;
    }

    let cancelled = false;

    const render = async () => {
      try {
        let diagramToRender = diagramText;
        if (parsedDiagram.subgraphs.length > 0) {
          diagramToRender = rebuildDiagram(parsedDiagram, Array.from(collapsedSubgraphs));
        }

        await mermaid.parse(diagramToRender);
        const { svg } = await mermaid.mermaidAPI.render(diagramId, diagramToRender);

        if (cancelled || !ref.current) return;

        cleanupSvgInteractions();
        ref.current.innerHTML = svg;

        const svgEl = ref.current.querySelector("svg");
        if (!svgEl) {
          if (ref.current) ref.current.innerHTML = "";
          return;
        }

        svgEl.setAttribute("preserveAspectRatio", "xMinYMin meet");
        svgEl.style.maxWidth = "none";
        svgEl.style.height = "auto";
        svgEl.style.overflow = "visible";
        svgEl.style.display = "block";

        attachSvgInteractions();
        attachClusterClickToggles(svgEl);

        const clusterBorders = svgEl.querySelectorAll("g.cluster rect, g.cluster polygon, g.cluster path");
        clusterBorders.forEach((shape) => {
          shape.style.stroke = "#e2e8f0";
          shape.style.strokeWidth = "1px";
        });

        const anchor = pendingAnchorRef.current;
        if (anchor) {
          pendingAnchorRef.current = null;
          requestAnimationFrame(() => {
            setZoom((currentZoom) => {
              setPan({
                x: anchor.screenX - anchor.svgX * currentZoom,
                y: anchor.screenY - anchor.svgY * currentZoom,
              });
              return currentZoom;
            });
          });
        }

        if (autoResetDoneForKeyRef.current !== diagramKey) {
          autoResetDoneForKeyRef.current = diagramKey;
          requestAnimationFrame(() => {
            if (!cancelled) {
              handleReset();
            }
          });
        }
      } catch (err) {
        console.error("Mermaid render error:", err);
        if (ref.current) ref.current.innerHTML = "";
      }
    };

    render();

    return () => {
      cancelled = true;
      cleanupSvgInteractions();
    };
  }, [diagramText, modalKey, collapsedSubgraphs]);

  const startDrag = (x, y) => {
    setIsDragging(true);
    dragStartRef.current = { x, y };
    panStartRef.current = { ...pan };
    dragDistanceRef.current = 0;
    document.body.style.userSelect = "none";
  };

  const onPointerDown = (e) => {
    const x = e.clientX ?? (e.touches && e.touches[0]?.clientX);
    const y = e.clientY ?? (e.touches && e.touches[0]?.clientY);
    if (x != null && y != null) startDrag(x, y);
  };

  const onPointerMove = (e) => {
    if (!isDragging) return;
    const x = e.clientX ?? (e.touches && e.touches[0]?.clientX);
    const y = e.clientY ?? (e.touches && e.touches[0]?.clientY);
    if (x == null || y == null) return;
    const dx = x - dragStartRef.current.x;
    const dy = y - dragStartRef.current.y;
    dragDistanceRef.current = Math.max(dragDistanceRef.current, Math.hypot(dx, dy));
    setPan({ x: panStartRef.current.x + dx, y: panStartRef.current.y + dy });
  };

  const onPointerUp = () => {
    if (dragDistanceRef.current > DRAG_CLICK_THRESHOLD) {
      suppressClickUntilRef.current = Date.now() + 180;
    }
    setIsDragging(false);
    dragDistanceRef.current = 0;
    document.body.style.userSelect = "";
  };

  useEffect(() => {
    const wrapper = scaleWrapperRef.current;
    if (!wrapper) return undefined;

    const nativeWheelHandler = (event) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      event.stopPropagation();
      const direction = event.deltaY < 0 ? 1 : -1;
      setZoom((currentZoom) => {
        const nextZoom = direction > 0
          ? currentZoom * ZOOM_FACTOR
          : currentZoom / ZOOM_FACTOR;
        return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, +nextZoom.toFixed(4)));
      });
    };

    wrapper.addEventListener("wheel", nativeWheelHandler, { passive: false });
    return () => wrapper.removeEventListener("wheel", nativeWheelHandler);
  }, []);

  const handleZoomIn = () =>
    setZoom((z) => Math.min(MAX_ZOOM, +(z * ZOOM_FACTOR).toFixed(4)));
  const handleZoomOut = () =>
    setZoom((z) => Math.max(MIN_ZOOM, +(z / ZOOM_FACTOR).toFixed(4)));
  const handleReset = () => {
    setZoom(DEFAULT_ZOOM);
    setPan({ x: 0, y: 0 });
    setCollapsedSubgraphs(new Set());
    selectedSvgNodeRef.current = null;
    setSelectedNode(null);
    applyNodeHighlight(null);
  };

  return (
    <div className="relative w-full h-full flex">
      <div className="relative flex-1 h-full min-w-0">
        {parsedDiagram.subgraphs.length > 0 && (
          <div className="absolute left-3 top-3 z-20 p-2 flex flex-row gap-1">
            <Tooltip title={areAllSubgraphsCollapsed ? "Expand all groups" : "Collapse all groups"}>
              <IconButton
                size="medium"
                onClick={toggleAllSubgraphs}
                sx={{
                  padding: 2,
                  width: 32,
                  height: 32,
                  bgcolor: "#ffffff",
                  border: "1px solid #dbe2ea",
                  boxShadow: "0 1px 2px rgba(15,23,42,0.08)",
                  color: areAllSubgraphsCollapsed ? "#0f766e" : "#334155",
                  '&:hover': {
                    bgcolor: areAllSubgraphsCollapsed ? "#ecfdf5" : "#f8fafc",
                  },
                }}
              >
                {areAllSubgraphsCollapsed ? (
                  <UnfoldMoreRoundedIcon sx={{ fontSize: 20 }} />
                ) : (
                  <UnfoldLessRoundedIcon sx={{ fontSize: 20 }} />
                )}
              </IconButton>
            </Tooltip>
          </div>
        )}

        <div
          className="absolute right-3 z-20 flex flex-col gap-2"
          style={{ top: "12px" }}
        >
          <button onClick={handleZoomIn} className="p-2 bg-white border rounded shadow-sm hover:bg-slate-50">
            <ZoomIn className="h-4 text-gray-600" />
          </button>
          <button onClick={handleZoomOut} className="p-2 bg-white border rounded shadow-sm hover:bg-slate-50">
            <ZoomOut className="h-4 text-gray-600" />
          </button>
          <button onClick={handleReset} className="p-2 bg-white border rounded shadow-sm hover:bg-slate-50">
            <RotateCcw className="h-4 text-gray-600" />
          </button>
        </div>

        <div
          ref={scaleWrapperRef}
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
            cursor: isDragging ? "grabbing" : "grab",
          }}
          onMouseDown={onPointerDown}
          onMouseMove={onPointerMove}
          onMouseUp={onPointerUp}
          onMouseLeave={onPointerUp}
          onTouchStart={(e) => onPointerDown(e.touches ? e.touches[0] : e)}
          onTouchMove={(e) => onPointerMove(e.touches ? e.touches[0] : e)}
          onTouchEnd={onPointerUp}
        >
          <div
            ref={ref}
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transformOrigin: "center center",
            }}
          />
        </div>
      </div>

      <SummaryPanel
        panelOpen={panelOpen}
        setPanelOpen={setPanelOpen}
        selectedNode={selectedNode}
        setSelectedNode={(node) => {
          setSelectedNode(node);
          if (!node) {
            selectedSvgNodeRef.current = null;
            applyNodeHighlight(null);
          }
        }}
      />
    </div>
  );
}
